import * as t from 'io-ts';

import {RLogMessageContext} from '../../../srcCommon/services/logger/loggerCommon';
import {makeApiType, makeDefaultValueType} from '../api/apiTypes';

/*
 * Supported methods.
 */

export enum ElectronMethodsEnum {
  // Window.
  DID_ENTER_FULL_SCREEN = 'didEnterFullScreen',
  DID_LEAVE_FULL_SCREEN = 'didLeaveFullScreen',
  DID_CHANGE_ZOOM_FACTOR = 'didChangeZoomFactor',

  // Navigation.
  HISTORY_BACK = 'historyBack',
  HISTORY_FORWARD = 'historyForward',
  OPEN_HELP = 'showHelp',
  OPEN_MAILTO_URL = 'openMailto',
  OPEN_SETTINGS = 'openSettings',
  OPEN_URL = 'open',

  // Downloads.
  DOWNLOAD_DID_COMPLETE = 'downloadDidComplete',
  DOWNLOAD_DID_FAIL = 'downloadDidFail',
  DOWNLOAD_DID_PROGRESS = 'downloadDidProgress',

  // Miscellaneous.
  POST_MESSAGE = 'postMessage',
  PRINT = 'printCurrentConversation',
  SHOW_UPDATE_BANNER = 'showUpdateBanner',
  SIGNOUT = 'signout',
  TRACK_EVENT = 'trackEvent',
  OPEN_EML_FILE = 'openEMLFile',
  DATADOG_LOG = 'datadogLog',
  GRAMMARLY_AUTH = 'grammarlyAuth',
  HANDLE_SHARED_CONTENT = 'handleSharedContent',
}

/*
 * Runtime type.
 */

const RNoArguments = makeDefaultValueType(t.undefined, undefined);
const RElectronMethodsDirty = t.partial(
  {
    // Window.
    [ElectronMethodsEnum.DID_ENTER_FULL_SCREEN]: RNoArguments,
    [ElectronMethodsEnum.DID_LEAVE_FULL_SCREEN]: RNoArguments,
    [ElectronMethodsEnum.DID_CHANGE_ZOOM_FACTOR]: t.tuple([t.number]),
    // Navigation.
    [ElectronMethodsEnum.HISTORY_BACK]: RNoArguments,
    [ElectronMethodsEnum.HISTORY_FORWARD]: RNoArguments,
    [ElectronMethodsEnum.OPEN_HELP]: RNoArguments,
    [ElectronMethodsEnum.OPEN_MAILTO_URL]: t.tuple([t.string]),
    [ElectronMethodsEnum.OPEN_SETTINGS]: t.union([t.tuple([t.literal('identity')]), RNoArguments]),
    [ElectronMethodsEnum.OPEN_URL]: t.tuple([t.string]),
    // Downloads.
    [ElectronMethodsEnum.DOWNLOAD_DID_COMPLETE]: t.tuple([t.string, makeDefaultValueType(t.string, '')]),
    [ElectronMethodsEnum.DOWNLOAD_DID_FAIL]: t.tuple([t.string]),
    [ElectronMethodsEnum.DOWNLOAD_DID_PROGRESS]: t.tuple([t.string, makeDefaultValueType(t.number, 0)]),
    // Miscellaneous.
    [ElectronMethodsEnum.POST_MESSAGE]: t.tuple([t.string, t.unknown]),
    [ElectronMethodsEnum.PRINT]: RNoArguments,
    [ElectronMethodsEnum.SHOW_UPDATE_BANNER]: RNoArguments,
    [ElectronMethodsEnum.SIGNOUT]: RNoArguments,
    [ElectronMethodsEnum.TRACK_EVENT]: t.tuple([t.string, t.unknown]),
    [ElectronMethodsEnum.OPEN_EML_FILE]: t.tuple([t.string]),
    [ElectronMethodsEnum.DATADOG_LOG]: t.tuple([
      t.string,
      t.keyof({debug: null, info: null, warn: null, error: null}),
      t.union([RLogMessageContext, t.undefined]),
    ]),
    [ElectronMethodsEnum.GRAMMARLY_AUTH]: t.tuple([t.string]),
    [ElectronMethodsEnum.HANDLE_SHARED_CONTENT]: t.tuple([t.array(t.string), t.boolean]),
  },
  'ElectronMethods',
);

/*
 * Types.
 */

export interface ElectronMethodsModel extends t.TypeOf<typeof RElectronMethodsDirty> {}
export const RElectronMethods = t.clean<ElectronMethodsModel>(RElectronMethodsDirty as any);

/*
 * IO methods.
 */

function parseElectronMethods(src: any): ElectronMethodsModel {
  return src;
}

/*
 * API type.
 */

export const ApiElectronMethods = makeApiType('electronMethods', RElectronMethods, parseElectronMethods);
